import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useChatStore } from "@/stores/chatStore";
import { useNavStore } from "@/stores/navStore";
import { useSearchStore } from "@/stores/searchStore";
import { Button } from "@/components/common/Button";
import { fetchReportsOverview } from "@/api/reports";
import type { Conversation, ReportUser } from "@/api/types";
import { parseUTC } from "@/api/types";

// Sidebar resize bounds. Min matches the original Tailwind `w-64` so users
// can never drag the rail narrower than the designed minimum; max keeps it
// from eating the whole window on small displays.
const SIDEBAR_MIN_WIDTH = 256;
const SIDEBAR_MAX_WIDTH = 560;
const SIDEBAR_WIDTH_KEY = "cowork_sidebar_width";

function loadStoredWidth(): number {
  const raw = localStorage.getItem(SIDEBAR_WIDTH_KEY);
  const parsed = raw ? parseInt(raw, 10) : NaN;
  if (!Number.isFinite(parsed)) return SIDEBAR_MIN_WIDTH;
  return Math.max(SIDEBAR_MIN_WIDTH, Math.min(SIDEBAR_MAX_WIDTH, parsed));
}

function SkeletonItem() {
  return (
    <div className="px-3 py-2.5">
      <div className="h-4 shimmer rounded w-3/4 mb-1.5" />
      <div className="h-3 shimmer rounded w-1/3" />
    </div>
  );
}

/** Pulsing dot indicator for conversations with a running agent task. */
function RunningIndicator() {
  return (
    <span className="relative flex h-2 w-2 shrink-0" title="Agent running">
      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-jove-60 opacity-75" />
      <span className="relative inline-flex h-2 w-2 rounded-full bg-jove" />
    </span>
  );
}

/**
 * Steady amber dot for a conversation blocked on the user.
 *
 * Deliberately NOT animated, unlike {@link RunningIndicator}: the pulse reads as
 * "the machine is busy", and a row in this section is the exact opposite — the
 * machine has stopped and is waiting. Amber over the brand purple for the same
 * reason the running dot is purple: purple means "ours, in progress", amber means
 * "yours, now".
 */
function NeedsInputIndicator() {
  return (
    <span
      className="relative flex h-2 w-2 shrink-0"
      title="Waiting for your input"
    >
      <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-500 ring-2 ring-amber-500/25" />
    </span>
  );
}

/**
 * Pin glyph used both for the always-visible indicator on pinned rows and the
 * hover-revealed action button. ``filled`` flips between the solid (pinned)
 * and outline (unpinned hint) variants.
 *
 * Path adapted from the Lucide ``pin`` icon (ISC licensed) — a tilted thumbtack
 * which reads more clearly as "pin" than a generic bookmark.
 */
function PinIcon({ filled, className }: { filled: boolean; className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill={filled ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth={filled ? 1.25 : 1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 17v5" />
      <path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z" />
    </svg>
  );
}

/** Confirmation modal for deleting a conversation. */
function DeleteModal({
  title,
  onConfirm,
  onCancel,
}: {
  title: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onCancel} />

      {/* Dialog */}
      <div className="relative bg-surface rounded-xl shadow-xl border border-space-30 w-80 p-5 animate-[scaleIn_150ms_ease-out]">
        <h3 className="text-sm font-semibold text-space mb-1">Delete conversation</h3>
        <p className="text-xs text-space-50 mb-4 leading-relaxed">
          Are you sure you want to delete <span className="font-medium text-space">&ldquo;{title}&rdquo;</span>?
          This will permanently remove all messages and shortlists.
        </p>
        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-3 py-1.5 text-xs font-medium text-space-50 rounded-lg hover:bg-space-20 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-3 py-1.5 text-xs font-medium text-white bg-hydra rounded-lg hover:bg-hydra/90 transition-colors"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

export function Sidebar() {
  const {
    conversations,
    activeConversationId,
    runningTasks,
    loadConversations,
    openConversation,
    deleteConversation,
    togglePinConversation,
    updateConversation,
  } = useChatStore();
  const navigate = useNavigate();
  const location = useLocation();
  const mobileNavOpen = useNavStore((s) => s.mobileNavOpen);
  const closeMobileNav = useNavStore((s) => s.closeMobileNav);
  const openSearch = useSearchStore((s) => s.openSearch);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null);
  // Inline rename state — only one row is editable at a time. ``renameDraft``
  // holds the live input value so we can validate on commit (empty → cancel).
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState("");
  // Mobile row-action menu: the rename/pin/delete actions are hover-revealed
  // (unreachable by touch), so on mobile a kebab opens them as a dropdown.
  // Only one row's menu is open at a time.
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);

  // Admin "Others" tab (ADR-0009). ``isManager`` gates the tab strip and is
  // probed once per mount (fail-closed: non-admins get false and never see
  // it — including on the native app, where the backend always answers
  // false). The team list is fetched on each tab activation / filter change
  // so it's fresh without polling; rows are read-only (no rename/pin/delete
  // — not your chat).
  const [isManager, setIsManager] = useState(false);
  const [sidebarTab, setSidebarTab] = useState<"mine" | "others">("mine");
  const [reportUsers, setReportUsers] = useState<ReportUser[]>([]);
  const [ownerFilter, setOwnerFilter] = useState<string>("");
  const [reportsLoading, setReportsLoading] = useState(false);
  // Rows live in the store, not here: the WS handlers that converge the user's
  // own list converge these too, so a foreign chat's state (Working, title,
  // recap) tracks reality instead of freezing until the next reload.
  const reportConvs = useChatStore((s) => s.teamConversations);
  const loadTeamConversations = useChatStore((s) => s.loadTeamConversations);

  useEffect(() => {
    fetchReportsOverview()
      .then((o) => {
        setIsManager(o.is_manager);
        setReportUsers(o.reports);
      })
      .catch(() => setIsManager(false));
  }, []);

  const loadOthers = useCallback(
    (owner: string) => {
      setReportsLoading(true);
      void loadTeamConversations(owner).finally(() => setReportsLoading(false));
    },
    [loadTeamConversations],
  );

  // Non-admins can never land on the Others tab (e.g. role expired mid-session).
  const activeTab = isManager ? sidebarTab : "mine";

  // Resizable width — persisted to localStorage on drag release. ``widthRef``
  // mirrors state so the global mousemove handler always sees the latest
  // value without re-binding listeners on every render.
  const [sidebarWidth, setSidebarWidth] = useState<number>(loadStoredWidth);
  const widthRef = useRef(sidebarWidth);
  widthRef.current = sidebarWidth;
  const [resizing, setResizing] = useState(false);

  const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = widthRef.current;

    const onMove = (ev: MouseEvent) => {
      const next = Math.max(
        SIDEBAR_MIN_WIDTH,
        Math.min(SIDEBAR_MAX_WIDTH, startWidth + (ev.clientX - startX)),
      );
      setSidebarWidth(next);
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      setResizing(false);
      localStorage.setItem(SIDEBAR_WIDTH_KEY, String(widthRef.current));
    };

    document.body.style.cursor = "col-resize";
    // Suppress text selection during the drag so the cursor stays a
    // resize affordance and the user doesn't accidentally select chat list
    // content while flicking the handle around.
    document.body.style.userSelect = "none";
    setResizing(true);
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }, []);

  const startRename = (conv: Conversation) => {
    setRenamingId(conv.id);
    setRenameDraft(conv.title);
  };

  const cancelRename = () => {
    setRenamingId(null);
    setRenameDraft("");
  };

  const commitRename = (id: string, originalTitle: string) => {
    const next = renameDraft.trim();
    setRenamingId(null);
    setRenameDraft("");
    // No-op when empty or unchanged — keep the original title rather than
    // letting the user wipe it accidentally.
    if (!next || next === originalTitle) return;
    void updateConversation(id, { title: next });
  };

  const isGalleryView = location.pathname === "/artifacts";
  const isHomeView = location.pathname === "/" || location.pathname === "/home";

  // "New Conversation" no longer creates a chat directly — it routes to
  // the home page, where the user types the first message and the chat
  // input there does the create + open + send dance. The intent is the
  // same ("I want to start something new") but the home page is now the
  // canonical entry point.
  const handleNew = () => {
    navigate("/home");
  };

  useEffect(() => {
    loadConversations().finally(() => setLoading(false));
  }, [loadConversations]);

  // Auto-close the mobile drawer whenever the route changes — selecting a
  // conversation, hitting "New Conversation", or opening Artifacts all
  // navigate, so the drawer shouldn't linger over the freshly-loaded view.
  // No-op on desktop, where the rail is always visible and mobileNavOpen is
  // never set true.
  useEffect(() => {
    closeMobileNav();
  }, [location.pathname, closeMobileNav]);

  // Dismiss the mobile row-action menu on any outside tap (and on route change,
  // handled by the effect above resetting nothing — so close it here too).
  useEffect(() => {
    if (!menuOpenId) return;
    const onDown = () => setMenuOpenId(null);
    // Defer binding so the opening tap doesn't immediately close it.
    const id = window.setTimeout(() => document.addEventListener("pointerdown", onDown), 0);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener("pointerdown", onDown);
    };
  }, [menuOpenId]);

  const formatDate = (iso: string) => {
    const d = parseUTC(iso);
    const now = new Date();
    // Compare calendar dates in local timezone (not raw ms diff)
    const toDay = (dt: Date) => new Date(dt.getFullYear(), dt.getMonth(), dt.getDate()).getTime();
    const days = Math.round((toDay(now) - toDay(d)) / (1000 * 60 * 60 * 24));

    if (days === 0) return "Today";
    if (days === 1) return "Yesterday";
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  // Search now lives in the global Cmd/Ctrl+K modal (SearchModal), not inline
  // here — the sidebar always shows the full conversation list.
  const displayList: Conversation[] = conversations;

  // Three-way partition into the labelled sidebar sections: "Needs Input",
  // "Working", "Completed" (ADR-0017).
  //
  // Both states are derived SERVER-side and replicated on the conversation row,
  // so every window on every device partitions identically (#79) — the client
  // only chooses precedence, never re-derives the state.
  //
  //   Needs Input — a blocking prompt is outstanding (AskUserQuestion widget or
  //                 a tool approval). Checked FIRST because it is strictly
  //                 stronger: a blocked turn still holds its lease, so such a
  //                 chat is also ``agent_running`` and would otherwise vanish
  //                 into "Working" — which is precisely how this used to behave,
  //                 with an unread dot as the only hint that you were the one
  //                 holding it up.
  //   Working     — a live turn with nothing waiting on the user: locally
  //                 (instant ``runningTasks`` from this backend's task_status)
  //                 or anywhere, via the durable ``agent_running`` lease field
  //                 — OR outstanding background work, via ``open_subagents``
  //                 (ADR-0019). The third clause is not redundant: a delegated
  //                 run is deliberately NOT covered by the lease, which its
  //                 launching turn released on the way out, so between turns
  //                 this is the only thing that says the chat is still busy.
  //   Completed   — the catch-all, so nothing can be dropped.
  //
  // Pinning is now an ORDERING modifier rather than a fourth section: a pinned
  // chat floats to the top of whichever state it is actually in, keeping the
  // three sections a real state machine instead of mixing a user preference in
  // with agent status. The pin glyph on the row still marks it.
  const isNeedsInput = (c: Conversation) => !!c.needs_input;
  const isBusy = (c: Conversation) =>
    runningTasks.has(c.id) || !!c.agent_running || (c.open_subagents ?? 0) > 0;
  const isWorking = (c: Conversation) => !isNeedsInput(c) && isBusy(c);

  // Pinned-first within a section. The backend's ORDER BY already puts pinned
  // rows first across the whole list, but partitioning splits that ordering
  // apart, so each section re-applies it. Stable sort → the server's
  // recency ordering survives inside each half.
  const pinnedFirst = (list: Conversation[]) => [
    ...list.filter((c) => !!c.pinned_at),
    ...list.filter((c) => !c.pinned_at),
  ];

  // "Needs Input" orders by who has been waiting LONGEST (oldest stamp first) —
  // the opposite of every other section, which is recency-ordered. A chat that
  // has been blocked on you for two hours is more urgent than one blocked two
  // seconds ago, and it is the section you are meant to drain. Rows with no
  // stamp (an older peer that synced ``needs_input`` without it) sort last
  // rather than jumping the queue.
  const waitingLongestFirst = (list: Conversation[]) =>
    [...list].sort((a, b) => {
      if (!!a.pinned_at !== !!b.pinned_at) return a.pinned_at ? -1 : 1;
      const at = a.needs_input_at ? parseUTC(a.needs_input_at).getTime() : Infinity;
      const bt = b.needs_input_at ? parseUTC(b.needs_input_at).getTime() : Infinity;
      return at - bt;
    });

  const needsInputList = waitingLongestFirst(displayList.filter(isNeedsInput));
  const workingList = pinnedFirst(displayList.filter(isWorking));
  const completedList = pinnedFirst(
    displayList.filter((c) => !isNeedsInput(c) && !isWorking(c)),
  );

  const SectionLabel = ({ children }: { children: ReactNode }) => (
    <div className="px-4 pt-1 pb-2">
      <p className="text-[10px] uppercase tracking-wider text-space-40 font-medium">
        {children}
      </p>
    </div>
  );

  const renderItem = (conv: Conversation) => {
    const isActive = conv.id === activeConversationId && !isGalleryView;
    // Blocked beats running, matching the section precedence above — a chat
    // waiting on you holds its lease, so both would otherwise be true and the
    // row would show a pulsing "busy" dot for something that is entirely idle.
    const needsInput = !!conv.needs_input;
    // ``isWorking`` already carries the ``!needsInput`` half of that precedence,
    // so reusing it here keeps the dot and the section heading derived from ONE
    // expression — they cannot disagree, which is what a second hand-written
    // copy of the condition eventually does.
    const isRunning = isWorking(conv);
    const isPinned = !!conv.pinned_at;
    const isRenaming = renamingId === conv.id;
    // Active rows are being read right now — suppress the badge even if
    // the server hasn't acknowledged the mark-read POST yet, so the
    // sidebar doesn't briefly flash a "1" on the chat the user just
    // opened.
    const unread = !isActive && (conv.unread_count ?? 0) > 0;
    const unreadCount = conv.unread_count ?? 0;

    return (
      <div
        key={conv.id}
        role="button"
        tabIndex={0}
        onClick={() => { if (!isRenaming) openConversation(conv.id); }}
        onKeyDown={(e) => {
          if (isRenaming) return;
          if (e.key === "Enter" || e.key === " ") openConversation(conv.id);
        }}
        className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-all duration-150 group cursor-pointer
          ${
            isActive
              ? "bg-sel-surface text-sel-ink shadow-sm ring-1 ring-jove-60/30"
              : "text-space hover:bg-surface hover:shadow-sm"
          }`}
      >
        <div className="relative flex items-center gap-1.5">
          {/* Title row — full width at rest so long titles aren't truncated
              unnecessarily. ``pr-6`` is only applied for pinned rows because
              the small pin indicator sits inline (always visible); rename
              and delete actions float in absolutely on hover so they cost
              the title zero layout width when the user isn't hovering. */}
          <div className={`flex items-center gap-2 min-w-0 flex-1 ${isRenaming ? "" : "pr-7 md:pr-0"} ${isPinned && !isRenaming ? "md:pr-6" : ""}`}>
            {needsInput && <NeedsInputIndicator />}
            {isRunning && <RunningIndicator />}
            {isRenaming ? (
              <input
                autoFocus
                type="text"
                value={renameDraft}
                onChange={(e) => setRenameDraft(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === "Enter") {
                    e.preventDefault();
                    commitRename(conv.id, conv.title);
                  } else if (e.key === "Escape") {
                    e.preventDefault();
                    cancelRename();
                  }
                }}
                onBlur={() => commitRename(conv.id, conv.title)}
                className="flex-1 min-w-0 -my-0.5 px-1.5 py-0.5 text-sm font-medium rounded
                           bg-surface text-space border border-jove-60
                           focus:outline-none focus:ring-1 focus:ring-jove-60"
              />
            ) : (
              <span
                onDoubleClick={(e) => {
                  e.stopPropagation();
                  startRename(conv);
                }}
                className={`truncate ${
                  isActive
                    ? "font-medium text-sel-ink"
                    : unread
                      ? "font-semibold text-space"
                      : "font-medium"
                }`}
              >
                {conv.title}
              </span>
            )}
            {/* Location icon (ADR-0008): a subtle cloud (remote) or laptop
                (local) glyph so a user can tell at a glance where a chat runs. */}
            {!isRenaming && (
              <span
                className="shrink-0 text-space-40"
                title={conv.execution_location === "cloud" ? "Runs in the cloud" : "Runs on this device"}
              >
                {conv.execution_location === "cloud" ? (
                  <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15a4.5 4.5 0 004.5 4.5H18a3.75 3.75 0 001.332-7.257 3 3 0 00-3.758-3.848 5.25 5.25 0 00-10.233 2.33A4.502 4.502 0 002.25 15z" />
                  </svg>
                ) : (
                  <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0V12a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 12V5.25" />
                  </svg>
                )}
              </span>
            )}
          </div>
          {/* Inline pinned indicator — always visible on pinned rows so
              the user can spot (and unpin) a pin at rest. Lives outside
              the absolute hover group so it doesn't disappear when the
              cursor leaves the row. */}
          {isPinned && !isRenaming && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                togglePinConversation(conv.id);
              }}
              className="hidden md:block absolute right-0 top-1/2 -translate-y-1/2 p-1 rounded-md text-jove-80 hover:text-jove hover:bg-jove-10 transition-colors"
              title="Unpin"
              aria-label="Unpin conversation"
            >
              <PinIcon filled className="h-3 w-3" />
            </button>
          )}
          {/* Hover action group — absolute so it costs zero width at rest.
              Solid backdrop matching the row's bg-on-hover so the icons sit on
              a clean block over any truncated title text — no gradient fade
              (and it stays in step with the active row's `sel-surface`). */}
          {!isRenaming && (
            <div
              className={`absolute top-1/2 right-0 -translate-y-1/2 flex items-center gap-0.5 pl-2 pr-1 rounded-lg
                opacity-0 group-hover:opacity-100 transition-opacity duration-150
                pointer-events-none group-hover:pointer-events-auto
                ${isActive ? "bg-sel-surface" : "bg-surface"}`}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  startRename(conv);
                }}
                className="p-1 rounded-md text-space-40 hover:text-jove hover:bg-jove-10 transition-colors"
                title="Rename"
                aria-label="Rename conversation"
              >
                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  togglePinConversation(conv.id);
                }}
                className={`p-1 rounded-md transition-colors ${
                  isPinned
                    ? "text-jove-80 hover:text-jove hover:bg-jove-10"
                    : "text-space-40 hover:text-jove hover:bg-jove-10"
                }`}
                title={isPinned ? "Unpin" : "Pin"}
                aria-label={isPinned ? "Unpin conversation" : "Pin conversation"}
              >
                <PinIcon filled={isPinned} className="h-3 w-3" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteTarget({ id: conv.id, title: conv.title });
                }}
                className="p-1 rounded-md text-space-40 hover:text-hydra hover:bg-hydra-50 transition-colors"
                title="Delete"
                aria-label="Delete conversation"
              >
                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          )}
          {/* Mobile kebab — the hover action group above never activates on
              touch, so a tap opens rename/pin/delete as a dropdown. Desktop
              keeps the hover group; this whole affordance is md:hidden. */}
          {!isRenaming && (
            <div className="md:hidden">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuOpenId((cur) => (cur === conv.id ? null : conv.id));
                }}
                className="absolute right-0 top-1/2 -translate-y-1/2 p-1 rounded-md text-space-40 hover:text-space hover:bg-space-20 transition-colors"
                title="More actions"
                aria-label="Conversation actions"
                aria-haspopup="menu"
                aria-expanded={menuOpenId === conv.id}
              >
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <circle cx="12" cy="5" r="1.6" />
                  <circle cx="12" cy="12" r="1.6" />
                  <circle cx="12" cy="19" r="1.6" />
                </svg>
              </button>
              {menuOpenId === conv.id && (
                <div
                  role="menu"
                  onClick={(e) => e.stopPropagation()}
                  className="absolute right-0 top-full mt-1 z-20 w-36 rounded-lg border border-space-30 bg-surface shadow-card py-1 animate-[scaleIn_120ms_ease-out]"
                >
                  <button
                    role="menuitem"
                    onClick={(e) => { e.stopPropagation(); setMenuOpenId(null); startRename(conv); }}
                    className="w-full text-left px-3 py-2 text-xs text-space hover:bg-jove-10 transition-colors"
                  >
                    Rename
                  </button>
                  <button
                    role="menuitem"
                    onClick={(e) => { e.stopPropagation(); setMenuOpenId(null); togglePinConversation(conv.id); }}
                    className="w-full text-left px-3 py-2 text-xs text-space hover:bg-jove-10 transition-colors"
                  >
                    {isPinned ? "Unpin" : "Pin"}
                  </button>
                  <button
                    role="menuitem"
                    onClick={(e) => { e.stopPropagation(); setMenuOpenId(null); setDeleteTarget({ id: conv.id, title: conv.title }); }}
                    className="w-full text-left px-3 py-2 text-xs text-hydra hover:bg-hydra-50 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        {/* Recap — the one-line "where this chat is at" (ADR-0018). Sits between
            the title and the metadata row, indented to align with the title when
            a state dot is present. Hidden while renaming so the input isn't
            competing with two lines of context. Single-line + truncate: this is
            a scannable caption, and a wrapping one would make row heights jump
            around as recaps regenerate. */}
        {conv.recap && !isRenaming && (
          <p
            className={`mt-0.5 text-xs italic truncate ${needsInput || isRunning ? "ml-3.5" : ""} ${
              isActive ? "text-sel-meta" : "text-space-50"
            }`}
            title={conv.recap}
          >
            {conv.recap}
          </p>
        )}
        <div className={`flex items-center gap-2 mt-0.5 ${isRunning ? "ml-4" : ""}`}>
          <span className={`text-xs ${isActive ? "text-sel-meta" : "text-space-40"}`}>
            {formatDate(conv.updated_at)}
          </span>
          {conv.status === "completed" && (
            <span className="text-[10px] text-oxy font-medium bg-oxy-50 px-1.5 py-0.5 rounded-full">Done</span>
          )}
          {/* Unread badge — pill with the number of unread assistant
              messages. Capped at "9+" so a long background conversation
              doesn't blow out the row width. Hidden on hover so the
              rename/pin/delete action group can take over the right
              edge without overlap. */}
          {unread && (
            <span
              className="ml-auto text-[10px] font-semibold leading-none
                         min-w-[1.25rem] h-5 px-1.5 rounded-full
                         bg-primary text-on-primary inline-flex items-center justify-center
                         group-hover:opacity-0 transition-opacity"
              aria-label={`${unreadCount} unread message${unreadCount === 1 ? "" : "s"}`}
              title={`${unreadCount} unread`}
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <aside
      /* Desktop (`md`+): a static, resizable rail whose width is driven by the
         `--sidebar-w` CSS var (set from state below). Mobile: an off-canvas
         drawer — fixed, capped at 20rem / 85vw, sliding in from the left when
         `mobileNavOpen`, with a solid background and a raised shadow so it
         reads as an overlay over the dimmed content. The width lives in a CSS
         var (not an inline `width`) precisely so the mobile `w-[…]` class can
         win below `md` — an inline `width` would always beat the class. */
      className={`fixed inset-y-0 left-0 z-40 w-[min(85vw,20rem)]
                  md:relative md:z-auto md:w-[var(--sidebar-w)]
                  h-full flex flex-col border-r border-space-30
                  bg-space-20 md:bg-space-20/80 shrink-0
                  pt-[env(safe-area-inset-top)] md:pt-0
                  transition-transform duration-300 ease-out md:transition-none
                  ${mobileNavOpen ? "translate-x-0 shadow-2xl" : "-translate-x-full"} md:translate-x-0 md:shadow-none`}
      style={{ ["--sidebar-w" as string]: `${sidebarWidth}px` }}
    >
      {/* New conversation button — routes to ``/home`` rather than
          creating a chat directly. The home page's input is the
          single canonical entry point for starting a new conversation;
          its submit handler does the create + open + send dance. The
          active treatment mirrors a selected conversation row so the
          user gets the same "this is where I am" signal. */}
      <div className="p-3">
        <Button
          variant={isHomeView ? "primary" : "primary"}
          size="sm"
          className="w-full shadow-sm"
          onClick={handleNew}
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          New Conversation
        </Button>
      </div>

      {/* Search launcher — opens the global search modal (also Cmd/Ctrl+K).
          Styled like the old inline input so it reads as a search affordance,
          but it's a button: the actual search experience is the richer modal
          with All / Chats / Files tabs, snippets and thumbnails. */}
      <div className="px-3 pb-2">
        <button
          type="button"
          onClick={openSearch}
          className="group w-full flex items-center gap-2 pl-2.5 pr-2 py-1.5 text-xs rounded-lg
                     border border-space-30 bg-surface text-space-40
                     hover:border-jove-60 hover:text-space transition-all"
        >
          <svg
            className="h-3.5 w-3.5 shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <span className="flex-1 text-left">Search...</span>
          <kbd className="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded
                          border border-space-30 bg-space-20 text-[10px] font-medium text-space-40
                          group-hover:border-jove-60/40">
            ⌘K
          </kbd>
        </button>
      </div>

      {/* Admin tab strip (ADR-0009): your own chats vs the read-only team
          view. Rendered only for admins — everyone else sees no tabs at all. */}
      {isManager && (
        <div className="px-3 pb-2">
          <div className="flex rounded-lg bg-space-20 p-0.5" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "mine"}
              onClick={() => setSidebarTab("mine")}
              className={`flex-1 px-2 py-1 text-xs font-medium rounded-md transition-colors
                ${activeTab === "mine" ? "bg-surface text-space shadow-sm" : "text-space-40 hover:text-space"}`}
            >
              Your Chats
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "others"}
              onClick={() => { setSidebarTab("others"); loadOthers(ownerFilter); }}
              className={`flex-1 px-2 py-1 text-xs font-medium rounded-md transition-colors
                ${activeTab === "others" ? "bg-surface text-space shadow-sm" : "text-space-40 hover:text-space"}`}
            >
              Others
            </button>
          </div>
        </div>
      )}

      {/* Conversation list */}
      <div className="flex-1 overflow-y-auto scrollbar-on-hover px-2 pb-2">
        {activeTab === "others" ? (
          <>
            {/* Owner filter — narrow the team view to one person. */}
            <div className="px-2 pb-2">
              <select
                value={ownerFilter}
                onChange={(e) => {
                  setOwnerFilter(e.target.value);
                  loadOthers(e.target.value);
                }}
                aria-label="Filter by user"
                className="w-full text-xs rounded-lg border border-space-30 bg-surface text-space
                           px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-jove-60"
              >
                <option value="">All users</option>
                {reportUsers.map((u: ReportUser) => (
                  <option key={u.id} value={u.id}>{u.name || u.email}</option>
                ))}
              </select>
            </div>
            {reportsLoading ? (
              <div className="space-y-1"><SkeletonItem /><SkeletonItem /></div>
            ) : reportConvs.length === 0 ? (
              <p className="px-4 pb-2 text-xs text-space-40">
                No conversations from your team yet.
              </p>
            ) : (
              <div className="space-y-0.5">
                {reportConvs.map((conv) => {
                  const running = runningTasks.has(conv.id) || !!conv.agent_running;
                  return (
                    <div
                      key={conv.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => openConversation(conv.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") openConversation(conv.id);
                      }}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all duration-150 cursor-pointer
                        ${conv.id === activeConversationId
                          ? "bg-sel-surface text-sel-ink shadow-sm ring-1 ring-jove-60/30"
                          : "text-space hover:bg-surface hover:shadow-sm"}`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        {running && <RunningIndicator />}
                        <span className="truncate font-medium">{conv.title}</span>
                      </div>
                      <p className="mt-0.5 text-[11px] text-space-40 truncate">
                        {conv.owner_name || conv.owner_email}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        ) : loading ? (
          <>
            <SectionLabel>Conversations</SectionLabel>
            <div className="space-y-1">
              <SkeletonItem />
              <SkeletonItem />
              <SkeletonItem />
            </div>
          </>
        ) : displayList.length === 0 ? (
          <div className="text-center mt-10 px-4">
            <div className="w-10 h-10 rounded-xl bg-jove-30 flex items-center justify-center mx-auto mb-2.5">
              <svg className="w-5 h-5 text-jove-80" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
            </div>
            <p className="text-xs text-space-40 leading-relaxed">
              No conversations yet.<br />
              <span className="text-space-50">Start one to begin a conversation.</span>
            </p>
          </div>
        ) : (
          <>
            {/* Needs Input — chats blocked on the user, above everything else
                because they are the only ones that cannot move without them.
                The count is on the label rather than a badge elsewhere: this is
                a queue to drain, and its length is the useful number. */}
            {needsInputList.length > 0 && (
              <>
                <SectionLabel>
                  <span className="text-amber-600">
                    Needs Input · {needsInputList.length}
                  </span>
                </SectionLabel>
                <div className="space-y-0.5 mb-2">
                  {needsInputList.map((conv) => renderItem(conv))}
                </div>
              </>
            )}
            {/* Working — a live agent task with nothing waiting on the user
                (here or relayed from another environment). */}
            {workingList.length > 0 && (
              <>
                <SectionLabel>Working</SectionLabel>
                <div className="space-y-0.5 mb-2">
                  {workingList.map((conv) => renderItem(conv))}
                </div>
              </>
            )}
            {/* Completed — every remaining chat. Always the catch-all so
                nothing is dropped. Pinned chats float to the top of whichever
                section they land in rather than forming their own group. */}
            {completedList.length > 0 && (
              <>
                <SectionLabel>Completed</SectionLabel>
                <div className="space-y-0.5">
                  {completedList.map((conv) => renderItem(conv))}
                </div>
              </>
            )}
          </>
        )}

      </div>

      {/* Artifacts link */}
      <div className="p-3 border-t border-space-30">
        <button
          onClick={() => navigate("/artifacts")}
          className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm rounded-lg transition-all duration-150
            ${isGalleryView
              ? "bg-sel-surface text-sel-ink font-medium shadow-sm ring-1 ring-jove-60/30"
              : "text-space-50 hover:text-jove hover:bg-jove-10"
            }`}
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
          </svg>
          All Artifacts
        </button>
      </div>

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <DeleteModal
          title={deleteTarget.title}
          onConfirm={() => {
            deleteConversation(deleteTarget.id);
            setDeleteTarget(null);
          }}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      {/* Drag handle — sits on the right edge of the sidebar. The hit area
          is 6px wide for easy grabbing; the visible 1px line lives at the
          inner edge to align with the existing border and only fills in
          (jove tint) on hover/drag so it stays invisible at rest. */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize sidebar"
        onMouseDown={handleResizeMouseDown}
        onDoubleClick={() => {
          setSidebarWidth(SIDEBAR_MIN_WIDTH);
          localStorage.setItem(SIDEBAR_WIDTH_KEY, String(SIDEBAR_MIN_WIDTH));
        }}
        className="hidden md:block absolute top-0 right-0 h-full w-1.5 -mr-0.5 cursor-col-resize group z-10"
      >
        <div
          className={`absolute right-0 top-0 h-full w-px transition-colors ${
            resizing ? "bg-jove-60" : "bg-transparent group-hover:bg-jove-60/60"
          }`}
        />
      </div>
    </aside>
  );
}
